import "dayjs/locale/tr";

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { meAtom } from "./store/jotaiStore";
import { atom, useAtom, useSetAtom } from "jotai";
import { Loader } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { toast, ToastContainer } from "react-toastify";
import salonService from "./services/salonService";
import {
  canAttemptAccessRefresh,
  clearTokenData,
  getRefreshToken,
  saveTokenData,
  saveTokenDataRefresh,
} from "./services/tokenManager";
import authService from "./services/authService";
import { ThemeProvider } from "./components/ThemeContext";

import TempSite from "./store/tempSite.json";
import ErrorStatusFullPage from "./pages/error/ErrorStatusFullPage";
import { Salon } from "./interfaces/services/Salon";
import { isSalonAvailable } from "./utils/isSalonAvailible";
import Profile from "./pages/profile/Profile";
import { SalonStatusType } from "./enums/SalonStatusType";

export const ScrollToTop = lazy(() => import("./components/ScrollToTop"));
export const Main = lazy(() => import("./layouts/Main"));
export const Home = lazy(() => import("./pages/main/Home"));
export const Login = lazy(() => import("./pages/auth/Login"));
export const Register = lazy(() => import("./pages/auth/Register"));
export const OtpVerification = lazy(
  () => import("./pages/auth/OtpVerification"),
);
export const ProtectedSalonRoute = lazy(
  () => import("./components/ProtectedSalonRoute"),
);
export const Error404FullPage = lazy(
  () => import("./pages/error/Error404FullPage"),
);
export const AppointmentForm = lazy(
  () => import("./pages/main/AppointmentForm"),
);
export const Contact = lazy(() => import("./pages/main/Contact"));
export const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword")); // Add lazy import

export const settingsAtom = atom({
  backgroundColor: "rgba(255, 255, 255,1)",
  frontColor: "rgba(0, 0, 0, 1)",
});

function App() {
  const developmentMode = false;
  // process.env.NODE_ENV === "development";

  const [settings, setSettings] = useAtom(settingsAtom);

  const pathname = window.location.pathname;
  const firmaAdi = pathname.split("/")[1];

  const setMe = useSetAtom(meAtom);

  const [page, setPage] = useState<any>(null);
  const [navbar, setNavbar] = useState<any>(null);
  const [footer, setFooter] = useState<any>(null);
  const [allComponents, setAllComponents] = useState<any[]>([]);
  const [, setServicesComponents] = useState<any[]>([]);
  const [aboutUsComponents, setAboutUsComponents] = useState<any[]>([]);
  const [salon, setSalon] = useState<Salon | null>(null);

  const [admin, setAdmin] = useState<boolean>(false);

  const [, setContactComponents] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);

  const getSalon = async () => {
    if (!firmaAdi) return undefined;
    try {
      const resp = await salonService.getSalonWithPath(firmaAdi);
      setSalon(resp);
      return resp.code;
    } catch (error) {
      console.error("Salon alınamadı:", error);
      toast.error(
        "Salon bilgileri alınamadı. Lütfen salon adını kontrol edin.",
        { toastId: "salon-error" },
      );
      return undefined;
    }
  };

  const getSalonData = async () => {
    try {
      const salonCode = await getSalon();
      if (!salonCode) return;
      const resp = developmentMode
        ? TempSite
        : await salonService.getCustomSalonData(salonCode);
      setPage(resp.data);

      // Her şeyi resp.data'dan alıyoruz artık
      const data = resp.data;

      setSettings(data.settings.options || {});
      setNavbar(data.navbar || {});
      setFooter(data.footer || {});

      const all: any[] = [];
      const contact: any[] = [];
      const about: any[] = [];
      const services: any[] = [];

      Object.entries(data).forEach(([_, value]: any) => {
        all.push(value);
        switch (value.section) {
          case "contact":
            contact.push(value);
            break;
          case "about-us":
            about.push(value);
            break;
          case "services":
            services.push(value);
            break;
          default:
            break;
        }
      });

      setAllComponents(all);
      setContactComponents(contact);
      setAboutUsComponents(about);
      setServicesComponents(services);
    } catch (error) {
      toast.error(
        "Salon internet sayfası alınamadı. Lütfen salon adını kontrol edin.",
        { toastId: "salon-data-error" },
      );
      console.error("Salon verileri alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  const AdminControl = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokens = {
      accessToken:
        urlParams.get("accessToken") ?? localStorage.getItem("authToken") ?? "",
      accessExpire:
        urlParams.get("accessExpire") ??
        localStorage.getItem("accessExpire") ??
        "",
      refreshToken:
        urlParams.get("refreshToken") ??
        localStorage.getItem("refreshToken") ??
        "",
      refreshExpire:
        urlParams.get("refreshExpire") ??
        localStorage.getItem("refreshExpire") ??
        "",
      userCode:
        urlParams.get("userCode") ?? localStorage.getItem("userCode") ?? "",
    };

    if (
      tokens.accessToken &&
      tokens.userCode &&
      tokens.refreshToken &&
      tokens.refreshExpire &&
      tokens.accessExpire
    ) {
      try {
        saveTokenData(tokens);
        const me = await authService.me();
        setMe(me);
        if (me.type === "Admin") {
          setAdmin(true);
        }
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        console.error("Token doğrulanamadı:", error);
      }
    } else return;
  };

  useEffect(() => {
    AdminControl();
    setLoading(true);
    getSalonData();
  }, [firmaAdi]);

  const getMe = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokenData();
        return;
      }

      if (canAttemptAccessRefresh()) {
        const newTokens = await authService.refreshToken(refreshToken);
        saveTokenDataRefresh(newTokens);
      }

      const me = await authService.me();
      setMe(me);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    getMe();
  }, []);

  return (
    <ThemeProvider>
      <div
        className="App"
        style={{
          backgroundColor: settings?.backgroundColor || "#fff",
          color: settings?.frontColor || "#000",
        }}
      >
        <DatesProvider settings={{ locale: "tr", firstDayOfWeek: 1 }}>
          {loading ? (
            <div className="w-screen h-screen flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="w-screen h-screen flex items-center justify-center">
                  <Loader />
                </div>
              }
            >
              <BrowserRouter>
                <ScrollToTop />

                <Routes>
                  {!admin && !isSalonAvailable(salon?.status) ? (
                    <>
                      <Route
                        path="*"
                        element={
                          <ErrorStatusFullPage
                            status={salon?.status || "NotFound"}
                          />
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Route element={<Main navbar={navbar} footer={footer} />}>
                        <Route
                          path="/:firmaAdi"
                          element={
                            <ProtectedSalonRoute>
                              <Home componentList={allComponents} />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/hakkimizda"
                          element={
                            <ProtectedSalonRoute>
                              <Home componentList={aboutUsComponents} />
                              {settings && <Contact settings={settings} />}
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/giris-yap"
                          element={
                            <ProtectedSalonRoute>
                              <Login />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/sifremi-unuttum"
                          element={
                            <ProtectedSalonRoute>
                              <ForgotPassword />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/profil"
                          element={
                            <ProtectedSalonRoute>
                              <Profile />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/kayit-ol"
                          element={
                            <ProtectedSalonRoute>
                              <Register />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/telefon-aktivasyon"
                          element={
                            <ProtectedSalonRoute>
                              <OtpVerification />
                            </ProtectedSalonRoute>
                          }
                        />
                        <Route
                          path="/:firmaAdi/randevu-al"
                          element={
                            <ProtectedSalonRoute>
                              <AppointmentForm />
                            </ProtectedSalonRoute>
                          }
                        />
                      </Route>
                      <Route path="*" element={<Error404FullPage />} />
                    </>
                  )}
                </Routes>
                {admin && (
                  <div className="fixed bottom-0 left-0 z-[999] text-sm p-2 bg-zinc-950/50 text-white">
                    <p className="font-bold">Admin Log</p>
                    <p>
                      Salon status:{" "}
                      <span
                        className={`font-semibold ${
                          isSalonAvailable(salon?.status)
                            ? "text-green-300"
                            : "text-red-300"
                        }`}
                      >
                        {salon?.status}
                      </span>
                    </p>
                    <p>
                      Salon Type:{" "}
                      <span className={`font-semibold`}>
                        {salon?.salonType}
                      </span>
                    </p>
                    <p>
                      Contact:{" "}
                      <span className={`font-semibold`}>
                        {salon?.contactNumber}
                      </span>
                    </p>
                    <p>
                      <p>Code</p>
                      <p className={`font-semibold`}>{salon?.code}</p>
                    </p>
                  </div>
                )}
              </BrowserRouter>
            </Suspense>
          )}
        </DatesProvider>
        <ToastContainer />
      </div>
    </ThemeProvider>
  );
}

export default App;
